/* global crypto */

import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import cookies from 'lib/cookies'
// import { isProductionHost } from 'lib/env'
import { loginURI, logoutURI } from 'lib/constants'
import { useMutation } from '@apollo/client'
import AppError, { assert, logException, logExceptionDebug } from 'lib/errors'
import { appErrorVar } from 'lib/utils/store/cache'
import { ALMA_AUTH0_AUDIENCE, ALMA_AUTH0_CLIENT_ID, ALMA_AUTH0_DOMAIN, ALMA_AUTH0_LOGIN_CALLBACK_URL, ALMA_AUTH0_LOGOUT_CALLBACK_URL } from 'lib/alma/alma'
import { setInfoBreadcrumb } from 'lib/sentry'
import sessionStorage from 'lib/sessionStorage'
import localStorage from 'lib/localStorage'
import { debugLog } from 'lib/console'
import { withProfiler } from '@sentry/react'
import { handleVerticalBackUrl } from 'lib/verticalReferrer'
import { getLocale } from 'lib/utils/getLocale'
import { urlWithoutLoginRedirectParam, clearAuth0UserSession } from 'lib/helpers'
import { openModal } from 'lib/utils/store/modals'

import { AlmaAccountCore } from '@almamedia/alma-account-core'
import { AlmaAccountAnalytics } from '@almamedia/alma-account-analytics'

import verifyMapAlmaUser from 'graphql/user/verifyMapAlmaUser.gql'
import logoutAlmaUpsSession from 'graphql/user/logoutAlmaUpsSession.gql'

/**
 * Redirects the user to the alma login page.
 *
 * @param {Object} core - The core object used for authentication.
 * @param {Object} options - Options for the login redirect.
 * @param {string} options.language - The language to be used for the login page.
 * @param {string} options.source - The source of the login request.
 * @param {string} [options.email] - An optional email hint for the login page.
 */
const redirectToHostedLogin = (core, options) => {
    const url = new URL(window.location.href)
    debugLog('auth - redirectToHostedLogin - options:', options)
    const { language, source, email } = options

    // Add query param right before login redirect occurs, so in case user comes back (via back link/browser back)
    // we can display "login required message" instead of auto redirecting back to login and essentially
    // causing a loop where user is stuck.
    url.searchParams.set('loginRedirect', '1')

    window.history.replaceState(null, null, url)

    core.login({
        authorizationParams: {
            theme: 'light',
            lang: language,
            source: source,
            login_hint: email ?? '',
        },
    })
}

export const resetAuthSession = () => {
    cookies.remove('userLoggedIn')
    cookies.remove('sessionExpired')
}

export const startAuthSession = () => {
    resetAuthSession()
    cookies.set('userLoggedIn', 1)
}

export const AuthContext = React.createContext(null)

const LOGIN_STATE_NAME = 'loginState'

const loginStateExists = () => {
    return !!sessionStorage.getItem(LOGIN_STATE_NAME)
}

const generateStateKey = () => {
    return ([1e7] + 1e3 + 4e3 + 8e3 + 1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    )
}

export const getOrCreateLoginState = () => {
    let loginState = {}

    const loginStateStr = sessionStorage.getItem(LOGIN_STATE_NAME)
    if (loginStateStr && loginStateStr !== 'undefined') {
        loginState = JSON.parse(sessionStorage.getItem(LOGIN_STATE_NAME) || {})
    }

    return {
        successTarget: urlWithoutLoginRedirectParam(),
        failedTarget: '/',
        key: generateStateKey(),
        ...loginState,
    }
}

export const setLoginState = (loginState) => {
    const string = JSON.stringify(loginState)
    sessionStorage.setItem(LOGIN_STATE_NAME, string)
}

export const clearLoginState = () => {
    sessionStorage.removeItem(LOGIN_STATE_NAME)
}

const Auth = ({ children, history }) => {
    const { i18n } = useTranslation()

    const [initialAuthReady, setInitialAuthReady] = useState(false)
    const [userEmail, setUserEmail] = useState(null)
    const [almaAuthData, setAlmaAuthData] = useState(null)
    const [authenticated, setAuthenticated] = useState(null) // true if Alma and Nettix SSO auth both done
    const [almaAuthError, setAlmaAuthError] = useState(null)
    const [verifyMapAlmaUserResponseError, setVerifyMapAlmaUserResponseError] = useState(null)
    const [actions, setActions] = useState(null)
    const [almaLoading, setAlmaLoading] = useState(null)

    // make sure up-to-date actions are available in any functions/callbacks as actions state might not update
    const actionsRef = useRef(null)
    actionsRef.current = actions

    const pathname = window.location.pathname
    const [logoutCallback] = useState(pathname === '/logout/callback')
    const [ssoLoginSuccess, setSsoLoginSuccess] = useState(pathname === '/login/sso/callback/success')
    const [ssoLoginFailed, setSsoLoginFailed] = useState(pathname === '/login/sso/callback/failed')
    const [ssoLogoutSuccess] = useState(pathname === '/logout/sso/callback/success')
    const [ssoLogoutFailed] = useState(pathname === '/logout/sso/callback/failed')

    // const [userReturnedFromLoginPage] = useState(window.location.search.indexOf('loginRedirect=1') !== -1)
    // const [loginAttemptedForUnsupportedBrowser] = useState(cookies.get('mv-try-alma-login'))

    const [verifyMapAlmaUserMutation, { loading: almaLoginLoading, error: verifyMapAlmaUserError }] = useMutation(verifyMapAlmaUser)
    const [logoutAlmaUpsSessionMutation] = useMutation(logoutAlmaUpsSession)

    handleVerticalBackUrl()

    /**
     * This function gets the singleton instance of the AlmaAccountCore, which is used for authentication purposes.
     *
     * @returns {AlmaAccountCore} - The instance of the AlmaAccountCore.
     */
    const core = AlmaAccountCore.getInstance()

    /**
     * Gets the singleton instance of AlmaAccountAnalytics.
     *
     * This function initializes and returns the AlmaAccountAnalytics instance with the provided client ID.
     *
     * @type {AlmaAccountAnalytics}
     */
    const analytics = AlmaAccountAnalytics.getInstance({
        clientId: ALMA_AUTH0_CLIENT_ID,
    })

    /**
     * Initializes the AlmaAccountCore instance with the provided configuration.
     *
     * @param {Object} core - The AlmaAccountCore instance to be initialized.
     * @returns {Promise<void>} - A promise that resolves when the core is successfully initialized.
     * @throws {Error} - Throws an error if the initialization fails.
     */
    const initializeCore = async (core) => {
        try {
            await core.init({
                domain: ALMA_AUTH0_DOMAIN,
                clientID: ALMA_AUTH0_CLIENT_ID,
                audience: ALMA_AUTH0_AUDIENCE,
                redirectUri: ALMA_AUTH0_LOGIN_CALLBACK_URL,
                scope: 'openid profile email',
                realm: 'Almatunnus',
            })
        } catch (error) {
            logExceptionDebug('Error initializing core: ', error)
        }
    }

    /**
     * Checks the session and retrieves the token if necessary.
     *
     * If the user is authenticated, it handles the login callback.
     * If the user is not authenticated and a Nettix token exists, it attempts to get an access token using a popup. Afterward, it handles the login callback.
     *
     * @param {Object} core - The core object used for authentication.
     * @param {Object} analytics - The analytics object used for tracking events.
     * @param {boolean} nettixTokenExist - Indicates whether a Nettix token exists.
     * @returns {Promise<void>} - A promise that resolves when the session check and token retrieval are complete.
     */
    const checkSessionAndGetToken = async (core, analytics, nettixTokenExist) => {
        try {
            const status = await core.isAuthenticated()

            if (!status && nettixTokenExist) {
                const token = await core.getAccessToken() && await core.getAccessToken({ cacheMode: 'off' })
                !token && openModal('auth-login-confirm-modal')
            }
            await handleLoginCallback(core, analytics, !!status || nettixTokenExist)
        } catch (error) {
            logExceptionDebug('Error checking session or getting token: ', error)
        }
    }

    const clientLogoutAuth0 = () => {
        setAlmaAuthData(null)

        localStorage.removeItem('ALMA')
        localStorage.removeItem('almad-gravito_microprofile')
        cookies.remove('auth0')
        cookies.remove('auth0.ssodata')
        cookies.remove('_auth0.ssodata_compat')
    }

    const ssoLogin = (idToken) => {
        // alma-token is used by /sso/login
        cookies.set('alma-token', idToken, { maxAge: 10 })

        if (!loginStateExists()) {
            setLoginState({
                ...getOrCreateLoginState(),
                successTarget: urlWithoutLoginRedirectParam(),
                failedTarget: `${getLocale(i18n.language) || '/'}`,
            })
        }

        const origin = window.location.origin

        // Callback url's that SSO will call when login was success
        const successCallbackUrl = `${origin}/login/sso/callback/success`
        const failedCallbackUrl = `${origin}/login/sso/callback/failed`
        window.location.replace(`${loginURI}?successUrl=${successCallbackUrl}&failureUrl=${failedCallbackUrl}`)
    }

    const ssoLogout = () => {
        const origin = window.location.origin

        // Callback url's that SSO will call when login was success
        const successCallbackUrl = `${origin}/logout/sso/callback/success`
        const failedCallbackUrl = `${origin}/logout/sso/callback/failed`
        window.location.replace(`${logoutURI}?successUrl=${successCallbackUrl}&failureUrl=${failedCallbackUrl}`)
    }

    const setAppAuthenticated = (authenticated) => {
        if (authenticated) {
            startAuthSession()
            setAuthenticated(true)
        } else {
            resetAuthSession()
            setAuthenticated(false)
        }
    }

    const appAuthenticate = async (idToken, isLogin = false, acceptTos = false) => {
        await verifyMapAlmaUserMutation({
            variables: {
                almaToken: idToken || '',
                acceptTos: acceptTos,
                forceLogoutNettixSso: ssoLogoutFailed,
                isLogin,
                isLogout: logoutCallback,
            },
        })
            .then(async (result) => {
                const { type, message, email } = result.data.verifyMapAlmaUser

                let sessionSetSuccessfully = false

                switch (type) {
                case 'error':
                    assert(message, 'verifyMapAlmaUser - Message is required in type "error"')
                    setVerifyMapAlmaUserResponseError(message)
                    break
                case 'login':
                    // avoid endlessly calling ssoLogin
                    if (!ssoLoginFailed && !ssoLoginSuccess) {
                        cookies.set('sso-login', 1, { maxAge: 30 })
                        ssoLogin(idToken)
                        return // sso login will do redirection
                    }
                    break
                case 'require_tos':
                    // cookies.set('alma-register-tos-modal', 1, { maxAge: 30 })
                    setAlmaLoading(false)
                    openModal('auth-tos-modal')
                    break
                case 'sso_logout_required':
                    if (!ssoLogoutFailed && !ssoLogoutSuccess) { // avoid endlessly calling ssoLogout
                        ssoLogout()
                        return // sso logout will do redirection
                    }
                    break
                case 'no_ups_sessions':
                case 'no_valid_ups_sessions':
                case 'not_personal_account':
                case 'not_personal_account_alma_jwt':
                    assert(!logoutCallback, `verifyMapAlmaUser - Response type "${type}" not expected after logout callback.`)
                    clientLogoutAuth0()

                    if (message) {
                        setVerifyMapAlmaUserResponseError(message)
                    }
                    break
                case 'token_mismatch':
                case 'no_session':
                    clearAuth0UserSession()
                    break
                case 'nettix_token_valid':
                    if (!cookies.get('sso-login') && cookies.get('alma-authenticated')) {
                        cookies.remove('sso-login')
                        cookies.remove('alma-authenticated')
                        window.location.reload()
                    }
                    cookies.remove('sso-login')
                    cookies.remove('alma-authenticated')

                    setUserEmail(email)
                    sessionSetSuccessfully = true
                    break
                default:
                    logException(`Unhandled verifyMapAlmaUserMutation type "${type}"`)
                }

                if (sessionSetSuccessfully) {
                    setAppAuthenticated(true)
                } else {
                    setAppAuthenticated(false)
                }
            })
    }

    /**
     * Handles the login callback process.
     *
     * This function retrieves user information and ID token from the provided core instance if authenticated.
     * It then sets the Alma authentication data and calls the appAuthenticate function.
     * If an error occurs, it logs the error and calls appAuthenticate with an empty token.
     *
     * @param {Object} core - The core object used for authentication.
     * @param {Object} analytics - The analytics object used for tracking events.
     * @param {boolean} [authenticated=false] - Indicates whether the user is authenticated.
     * @returns {Promise<void>} - A promise that resolves when the login callback process is complete.
     */
    const handleLoginCallback = async (core, analytics, authenticated = false, callEvent = false) => {
        try {
            if (core && authenticated) {
                const user = await core.getUserInfo()
                const idToken = await core.getIdToken({ raw: true })

                /**
                 * Sends a user event to AlmaAccountAnalytics.
                 *
                 * @param {Object} user - The user object containing user information.
                 * @param {Object} analytics - The AlmaAccountAnalytics instance used to send the user event.
                 */
                callEvent && user && analytics && analytics.sendUserEvent({ user: user })

                if (!user?.email_verified) {
                    setAlmaLoading(false)
                }

                setAlmaAuthData({ user, idToken })
                await appAuthenticate(idToken ?? '', !!idToken)
            } else {
                setAlmaAuthData({ user: false, idToken: null })
                authenticated === null && await appAuthenticate('')
            }
        } catch (error) {
            setAlmaAuthError(new Error(error))
            logException(error)
            await appAuthenticate('')
        }
    }

    const handleActions = (core, analytics, actions) => {
        // Add new and extend existing Alma Tunnus component actions
        const authActions = {
            ...actions,
            redirectToHostedLogin: (options) => redirectToHostedLogin(core, options),
            appAuthenticate,
            popupLogin: (options) => {
                // Callback function to be run after popup login is done. This is used to check if user is authenticated
                const loginCallback = async () => {
                    const isAuthenticated = await core.isAuthenticated()
                    if (isAuthenticated) {
                        cookies.set('alma-authenticated', 1, { maxAge: 30 })
                        setSsoLoginSuccess(false)
                        setSsoLoginFailed(false)
                        setAlmaLoading(true)
                        await handleLoginCallback(core, analytics, true, true)
                    } else {
                        const url = new URL(window.location.href)

                        url.searchParams.set('loginRedirect', '1')

                        window.history.replaceState(null, null, url)
                        history.push(url.pathname + url.search)
                    }
                }

                const { language, source, email } = options

                core.popupLogin({
                    authorizationParams: {
                        theme: 'light',
                        lang: language,
                        source: source,
                        login_hint: email ?? '',
                    },
                })
                    .then(() => loginCallback())
                    .catch(error => {
                        logExceptionDebug('Error during login: ', error)
                    })
            },
            logout: async () => {
                setAlmaLoading(true)

                clearLoginState()

                const logout = async () => {
                    const user = await core.getUserInfo()

                    /**
                     * Sends a logout event to AlmaAccountAnalytics.
                     *
                     * This function sends a logout event to the AlmaAccountAnalytics instance with the user's Alma ID hash, connection, and language.
                     *
                     * @param {Object} analytics - The AlmaAccountAnalytics instance used to send the logout event.
                     * @param {Object} user - The user object containing user information.
                     * @param {string} user.almaIDHash - The Alma ID hash of the user.
                     * @param {string} user.connection - The connection type of the user.
                     * @param {string} user.lang - The language preference of the user.
                     * @returns {Promise<void>} - A promise that resolves when the logout event is sent.
                     */
                    await analytics.sendLogout({
                        almaIDHash: user?.['https://almamedia.net/almaIDHash'],
                        connection: user?.['https://almamedia.net/connection'],
                        lang: user?.['https://almamedia.net/lang'],
                    })

                    await core.logout(ALMA_AUTH0_LOGOUT_CALLBACK_URL)
                }

                try {
                    const idToken = await core.getIdToken({ raw: true })

                    if (idToken) {
                        await logoutAlmaUpsSessionMutation({ variables: { almaToken: idToken } })
                            .then(async () => {
                                // logout causes redirect
                                await logout()
                            })
                            .catch(async (e) => {
                                setInfoBreadcrumb('auth - UPS session logout failed')
                                logException(e)
                                await logout()
                            })
                    } else {
                        setInfoBreadcrumb('auth - UPS session call not possible! idToken not found')
                        await logout()
                    }
                } catch (e) {
                    setInfoBreadcrumb('auth - Logout error! Continue to Auth0 logout...')
                    logException(e)
                    await logout()
                }
            }
        }

        window.__mv_auth = window.__mv_auth || {}
        window.__mv_auth.actions = authActions

        setActions(authActions)
    }

    useEffect(() => {
        const initCore = async () => {
            if (core) {
                try {
                    await initializeCore(core)
                    await checkSessionAndGetToken(core, analytics, authenticated)

                    handleActions(core, analytics, actions)
                } catch (error) {
                    logExceptionDebug('Error during core initialization process: ', error)
                }
            }
        }

        initCore()
    }, [core, analytics, authenticated]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (almaAuthData?.user) {
            const currentUrl = new URL(window.location.href)

            if (currentUrl.searchParams.has('refreshSuccess') || currentUrl.searchParams.has('refreshFailed')) {
                cookies.set('refreshAttempted', 1, { maxAge: 120 })

                // Remove 'refreshSuccess' and 'refreshFailed' from the URL
                currentUrl.searchParams.delete('refreshSuccess')
                currentUrl.searchParams.delete('refreshFailed')

                // Push the updated URL to the browser's history without reloading the page
                window.history.replaceState({}, document.title, currentUrl.toString())
            }
        }

        if (verifyMapAlmaUserError) {
            logException(verifyMapAlmaUserError)
            const appError = AppError.readFromApolloError(verifyMapAlmaUserError)
            // appError.displayMethod = ERROR_DISPLAY_METHOD.NOTIFY
            appErrorVar(appError)
            setAppAuthenticated(false)
            setTimeout(async () => {
                if (actions.almaAuthData?.user) {
                    await actions.logout()
                }
            }, 4000)
        }
    }, [actions, verifyMapAlmaUserError, almaAuthData])

    const ready = actions && authenticated !== null && !almaLoginLoading

    useEffect(() => {
        if (ready && !initialAuthReady) {
            setInitialAuthReady(true)
        }
    }, [ready, setInitialAuthReady, initialAuthReady])

    return (
        <AuthContext.Provider value={{ actions, almaAuthData, almaAuthError, verifyMapAlmaUserResponseError, almaLoading, userEmail }}>
            {!!initialAuthReady && children}
        </AuthContext.Provider>
    )
}

Auth.propTypes = {
    children: PropTypes.node.isRequired,
    language: PropTypes.string.isRequired,
    allowGa: PropTypes.bool.isRequired,
}

export default withProfiler(Auth, { name: 'Auth' })
